
import React from 'react';
import { Input } from "@/components/ui/input";
import { LabelWithChildren } from '@/components/ui/label-with-children';
import { 
  Select, 
  SelectValue 
} from '@/components/ui/select';
import { 
  SelectTriggerWithChildren, 
  SelectContentWithChildren, 
  SelectItemWithChildren 
} from '@/components/ui/select-with-children';

export interface EarlyAccessFormFieldsProps {
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  referralSource?: string;
  accountType?: string;
  userRole?: string;
  implementationTimeline?: string;
  name?: string; // Added for compatibility
  handleFirstNameChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleLastNameChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEmailChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInvestorTypeChange?: (value: string) => void;
  onUpdateField?: (field: string, value: string) => void;
  onAccountTypeChange?: (type: string) => void;
}

export const EarlyAccessFormFields: React.FC<EarlyAccessFormFieldsProps> = ({
  firstName = '',
  lastName = '',
  email = '',
  company = '',
  referralSource = '',
  accountType = 'individual',
  userRole = '',
  implementationTimeline = '',
  name = '',
  handleFirstNameChange,
  handleLastNameChange,
  handleEmailChange,
  handleInvestorTypeChange,
  onUpdateField,
  onAccountTypeChange
}) => {
  // Handle different prop patterns
  const onFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (handleFirstNameChange) {
      handleFirstNameChange(e);
    } else if (onUpdateField) {
      onUpdateField('firstName', e.target.value);
    }
  };

  const onLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (handleLastNameChange) {
      handleLastNameChange(e);
    } else if (onUpdateField) {
      onUpdateField('lastName', e.target.value);
    }
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (handleEmailChange) {
      handleEmailChange(e);
    } else if (onUpdateField) {
      onUpdateField('email', e.target.value);
    }
  };

  const onAccountTypeSelect = (value: string) => {
    if (handleInvestorTypeChange) {
      handleInvestorTypeChange(value);
    } else if (onAccountTypeChange) {
      onAccountTypeChange(value);
    }
  };

  const firstNameValue = firstName || (name ? name.split(' ')[0] : '');
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <LabelWithChildren htmlFor="firstName" className="text-sm font-medium text-gray-300">
          First Name
        </LabelWithChildren>
        <Input
          type="text"
          id="firstName"
          placeholder="Enter your first name"
          value={firstNameValue}
          onChange={onFirstNameChange}
          required
          className="bg-gray-700/60 text-gray-50 placeholder-gray-400 border-gray-600 focus-visible:ring-blue-500"
        />
      </div>
      
      <div className="space-y-2">
        <LabelWithChildren htmlFor="lastName" className="text-sm font-medium text-gray-300">
          Last Name
        </LabelWithChildren>
        <Input
          type="text"
          id="lastName"
          placeholder="Enter your last name"
          value={lastName}
          onChange={onLastNameChange}
          required
          className="bg-gray-700/60 text-gray-50 placeholder-gray-400 border-gray-600 focus-visible:ring-blue-500"
        />
      </div>
      
      <div className="space-y-2">
        <LabelWithChildren htmlFor="email" className="text-sm font-medium text-gray-300">
          Email Address
        </LabelWithChildren>
        <Input
          type="email"
          id="email"
          placeholder="Enter your email address"
          value={email}
          onChange={onEmailChange}
          required
          className="bg-gray-700/60 text-gray-50 placeholder-gray-400 border-gray-600 focus-visible:ring-blue-500"
        />
      </div>
      
      <div className="space-y-2">
        <LabelWithChildren htmlFor="investorType" className="text-sm font-medium text-gray-300">
          Investor Type
        </LabelWithChildren>
        <Select value={accountType} onValueChange={onAccountTypeSelect}>
          <SelectTriggerWithChildren className="w-full bg-gray-700/60 border-gray-600 text-gray-50">
            <SelectValue placeholder="Select investor type" />
          </SelectTriggerWithChildren>
          <SelectContentWithChildren className="w-full">
            <SelectItemWithChildren value="individual">Individual Investor</SelectItemWithChildren>
            <SelectItemWithChildren value="professional">Professional</SelectItemWithChildren>
            <SelectItemWithChildren value="institutional">Institutional</SelectItemWithChildren>
          </SelectContentWithChildren>
        </Select>
      </div>
      
    </div>
  );
};
